import { TypeIcon } from "../common/TypeIcon";
import { useEffect, useMemo, useState } from "react";
import { useEntities } from "../../hooks/useEntities";
import { useNearbyEntities, type NearbyResult } from "../../hooks/useNearbyEntities";
import { useLiveWaitTime, type LiveWaitTime } from "../../hooks/useLiveWaitTime";
import { formatDistance, haversineMeters } from "../../utils/locationSharing";
import { partitionByActive, type TimeWindow } from "../../utils/entityActive";
import type { Entity, EntityType } from "../../types";

/** Short live-status label for a nearby card: wait, status, or next showtime. */
function nearbyLiveLabel(live: LiveWaitTime): { text: string; tone: "ok" | "warn" | "muted" } | null {
  if (live.loading) return null;
  const { waitMinutes, status, showtimes } = live;
  if (typeof waitMinutes === "number" && (!status || status === "OPERATING" || status === "UNKNOWN")) {
    return { text: `${waitMinutes}m wait`, tone: waitMinutes >= 60 ? "warn" : "ok" };
  }
  if (status === "DOWN") return { text: "Down", tone: "warn" };
  if (status === "CLOSED") return { text: "Closed", tone: "muted" };
  if (status === "REFURBISHMENT") return { text: "Refurb", tone: "muted" };
  if (showtimes && showtimes.length > 0) {
    const now = Date.now();
    const next =
      showtimes.find((s) => s.startTime && new Date(s.startTime).getTime() >= now) ?? showtimes[0];
    const d = next?.startTime ? new Date(next.startTime) : null;
    if (d && !Number.isNaN(d.getTime())) {
      return { text: `Next ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`, tone: "ok" };
    }
    return { text: "Showtimes", tone: "ok" };
  }
  return null;
}

function NearbyCard({
  entity,
  distanceMeters,
  onPick,
  disabled,
  back,
}: {
  entity: Entity & { id: string };
  distanceMeters: number;
  onPick: (entityId: string, type: EntityType) => void;
  disabled?: boolean;
  /** The hotel you're staying at: tapping it logs coming back. */
  back?: boolean;
}) {
  const live = useLiveWaitTime(entity);
  const label = nearbyLiveLabel(live);
  return (
    <button
      type="button"
      className={`nearby-card${back ? " nearby-card-back" : ""}`}
      onClick={() => onPick(entity.id, entity.type)}
      disabled={disabled}
      aria-label={back ? `Back at ${entity.displayName}` : undefined}
    >
      {back && <span className="nearby-card-back-tag">Back</span>}
      <span className="nearby-card-head">
        <span className="nearby-card-emoji">
          {entity.imageUrl ? <img src={entity.imageUrl} alt="" /> : <TypeIcon type={entity.type} name={entity.displayName} tile />}
        </span>
        <span className="nearby-card-dist">{formatDistance(distanceMeters)}</span>
      </span>
      <span className="nearby-card-name" title={entity.displayName}>{entity.displayName}</span>
      {label && (
        <span className="nearby-card-foot">
          <span className={`nearby-card-live nearby-card-live-${label.tone}`}>{label.text}</span>
        </span>
      )}
    </button>
  );
}

interface NearbyEntitiesProps {
  /** Invoked when the user taps a nearby place. */
  onPick: (entityId: string, type: EntityType) => void;
  disabled?: boolean;
  /** Max number of places to show (default 10, matching the Wear OS app). */
  limit?: number;
  /**
   * The stretch of time to judge "is this still a place" by — from
   * `useActiveAsOf`, so this strip and the picker above it agree. Omitted
   * means unbounded, i.e. no active-days filtering at all.
   */
  activeDuring?: TimeWindow;
  /** Distance cutoff in metres (default 25000). */
  maxDistanceMeters?: number;
  /** Label for the opt-in button. */
  promptLabel?: string;
  /**
   * The hotel of the stay you're out of (see `utils/hotelStays`). When you're
   * close to it, it takes the first card, tagged "Back", and tapping it calls
   * `onReturn` instead of `onPick`.
   */
  returnTo?: { entityId: string; onReturn: () => void };
  /** Told whether the `returnTo` hotel is showing here, so Recent can leave it out. */
  onReturnNear?: (near: boolean) => void;
  /** Told the ranked places once they're known, so the sheet can lead with the closest. */
  onNearby?: (nearby: NearbyResult<Entity & { id: string }>[]) => void;
}

/** How close the hotel has to be to count as "walking back in". */
const RETURN_NEAR_METERS = 600;

/**
 * Opt-in "what's near me" picker. The device location is only requested after
 * the user taps the prompt, and the full entity list is only loaded then too.
 */
export function NearbyEntities(props: NearbyEntitiesProps) {
  // Auto-load nearby places on mount — the geolocation prompt still gates the
  // actual device access, and NearbyEntitiesList shows a graceful message if
  // permission is denied or unavailable.
  const [enabled, setEnabled] = useState(true);

  if (!enabled) {
    return (
      <button
        type="button"
        className="log-activity-link"
        onClick={() => setEnabled(true)}
        disabled={props.disabled}
      >
        📍 {props.promptLabel ?? "Find places near me"}
      </button>
    );
  }

  return <NearbyEntitiesList {...props} />;
}

function NearbyEntitiesList({
  onPick,
  disabled,
  limit = 10,
  maxDistanceMeters = 25000,
  activeDuring,
  returnTo,
  onReturnNear,
  onNearby,
}: NearbyEntitiesProps) {
  const { entities, loading: entitiesLoading } = useEntities();
  // Filtered before ranking, not after: last year's haunted houses sit inside
  // the park's own geofence, so they'd otherwise take the nearest slots and
  // trimming them afterwards would just leave the strip short.
  const candidates = useMemo(
    () => (activeDuring ? partitionByActive(entities, activeDuring).active : entities),
    [entities, activeDuring],
  );
  const { nearby, position, loading, error } = useNearbyEntities(candidates, {
    enabled: true,
    limit,
    maxDistanceMeters,
  });
  // The hotel you're staying at, measured on its own — at a resort its
  // restaurants and pools can fill the ten nearest slots ahead of it.
  const returnHere = useMemo(() => {
    if (!returnTo || !position) return null;
    const hotel = entities.find((e) => e.id === returnTo.entityId);
    const loc = hotel?.location;
    if (!hotel || !loc || typeof loc.latitude !== "number" || typeof loc.longitude !== "number") return null;
    const distanceMeters = haversineMeters(position.lat, position.lng, loc.latitude, loc.longitude);
    return distanceMeters <= RETURN_NEAR_METERS ? { entity: hotel, distanceMeters } : null;
  }, [returnTo, position, entities]);
  useEffect(() => {
    onReturnNear?.(returnHere != null);
  }, [returnHere, onReturnNear]);
  useEffect(() => {
    onNearby?.(nearby);
  }, [nearby, onNearby]);
  const shown = useMemo(
    () =>
      returnHere
        ? [returnHere, ...nearby.filter((n) => n.entity.id !== returnHere.entity.id)].slice(0, limit)
        : nearby,
    [returnHere, nearby, limit],
  );

  // Still waiting on a fix or on the place list: show placeholders rather
  // than flashing "No known places nearby." first.
  const pending = !error && (loading || position === null || entitiesLoading);

  return (
    <div className="nearby-block">
      <h4 className="nearby-block-label">Near you</h4>
      {/* The slot keeps one card's height in every state, so the sheet
          doesn't change size (and shift taps) when the places arrive. */}
      <div className="nearby-slot nearby-slot-live" aria-busy={pending || undefined}>
        {pending ? (
          <NearbySkeleton lines={3} />
        ) : error ? (
          <p className="nearby-slot-msg text-muted">{error}</p>
        ) : shown.length === 0 ? (
          <p className="nearby-slot-msg text-muted">No known places nearby.</p>
        ) : (
          <div className="nearby-strip">
            {shown.map(({ entity, distanceMeters }) => {
              const back = returnHere?.entity.id === entity.id;
              return (
                <NearbyCard
                  key={entity.id}
                  entity={entity}
                  distanceMeters={distanceMeters}
                  onPick={back && returnTo ? () => returnTo.onReturn() : onPick}
                  disabled={disabled}
                  back={back}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Card-sized placeholders shown while a strip is loading. */
export function NearbySkeleton({
  label = "Finding nearby places…",
  lines = 2,
}: {
  label?: string;
  /** Text lines per placeholder card: 2 for name only, 3 with a status line. */
  lines?: 2 | 3;
}) {
  return (
    <div className="nearby-strip" role="status" aria-label={label}>
      {[0, 1, 2].map((i) => (
        <span key={i} className="nearby-card nearby-card-skeleton" aria-hidden="true">
          <span className="nearby-card-emoji" />
          <span className="nearby-skeleton-line" />
          <span className="nearby-skeleton-line nearby-skeleton-line-short" />
          {lines === 3 && <span className="nearby-skeleton-line nearby-skeleton-line-foot" />}
        </span>
      ))}
    </div>
  );
}
